import { useMemo, useState } from "react";
import { toZonedEvent } from "../MembershipViews";
import { alpha, cardCls, HomeLink, HomeAvatar, HomeEmpty, fmt, plural } from "./homeUi";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function RailCard({ icon, title, action, children, primary, tone }) {
  return (
    <section className={`${cardCls} p-4 sm:p-5`}>
      <div className="mb-4 flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={tone || { backgroundColor: alpha(primary, 0.12), color: primary }}>
          {icon}
        </span>
        <h3 className="min-w-0 flex-1 truncate text-[14.5px] font-bold text-gray-900">{title}</h3>
        {action || null}
      </div>
      {children}
    </section>
  );
}

const CalIcon = (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg>
);

/**
 * Month grid with a dot on every day that has an event, in the member's own
 * clock (toZonedEvent — the same conversion the Events page uses). Clicking a
 * marked day opens Events.
 */
export function HomeCalendar({ events, tz, primary, onOpenEvents }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const byDay = useMemo(() => {
    const map = {};
    for (const e of events || []) {
      const z = toZonedEvent(e, tz);
      if (!z.localIso) continue;
      (map[z.localIso] = map[z.localIso] || []).push(e);
    }
    return map;
  }, [events, tz]);

  const todayIso = ymd(new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const lead = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const shift = (n) => setCursor(new Date(year, month + n, 1));

  return (
    <RailCard icon={CalIcon} title="Events calendar" primary={primary}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[13px] font-semibold text-gray-800">{cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        <div className="flex items-center gap-0.5">
          {[[-1, "M15.75 19.5 8.25 12l7.5-7.5", "Previous month"], [1, "m8.25 4.5 7.5 7.5-7.5 7.5", "Next month"]].map(([n, d, label]) => (
            <button key={n} type="button" onClick={() => shift(n)} aria-label={label} className="grid h-7 w-7 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={d} /></svg>
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center tabular-nums">
        {DOW.map((d) => (
          <span key={d} className="py-1 text-[11px] font-semibold text-gray-400">{d}</span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`b${i}`} />;
          const iso = ymd(new Date(year, month, day));
          const evs = byDay[iso];
          const isToday = iso === todayIso;
          const past = iso < todayIso;
          return (
            <button
              key={iso}
              type="button"
              disabled={!evs}
              onClick={onOpenEvents}
              title={evs ? evs.map((e) => e.title).join(" · ") : undefined}
              className={`relative grid h-8 place-items-center rounded-lg text-[12.5px] font-medium transition ${
                isToday ? "font-bold" : past ? "text-gray-400" : "text-gray-700"
              } ${evs && !isToday ? "cursor-pointer hover:bg-gray-100" : ""} disabled:cursor-default`}
              style={isToday ? { backgroundColor: primary, color: "var(--brand-btn-text, #fff)" } : undefined}
            >
              {day}
              {evs ? (
                <span className="absolute bottom-1 h-1 w-1 rounded-full" style={{ backgroundColor: isToday ? "var(--brand-btn-text, #fff)" : primary }} />
              ) : null}
            </button>
          );
        })}
      </div>
    </RailCard>
  );
}

/** "in 4 days" / "tomorrow" / "today" — from the event's real instant. */
function countdown(z) {
  if (!z.localIso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = z.localIso.split("-").map(Number);
  const days = Math.round((new Date(y, m - 1, d) - today) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

export function HomeUpcoming({ events, tz, primary, onOpenEvents }) {
  const list = (events || []).slice(0, 3).map((e) => ({ e, z: toZonedEvent(e, tz) }));
  const Clock = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
  );
  return (
    <RailCard icon={Clock} title="Upcoming events" primary={primary} action={list.length ? <HomeLink primary={primary} onClick={onOpenEvents}>All</HomeLink> : null}>
      {list.length ? (
        <div className="space-y-2">
          {list.map(({ e, z }) => {
            const live = e.state === "live";
            const [, mo, dd] = String(z.localIso || "").split("-").map(Number);
            const monthLabel = mo ? new Date(2000, mo - 1, 1).toLocaleDateString("en-US", { month: "short" }).toUpperCase() : "";
            return (
              <button key={e.id} type="button" onClick={onOpenEvents} className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-2.5 text-left transition hover:border-gray-300 hover:bg-surface">
                <span className="w-12 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-surface text-center">
                  <span className="block py-0.5 text-[9.5px] font-bold tracking-[0.06em]" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>{monthLabel || "—"}</span>
                  <span className="block py-1 text-[18px] font-extrabold leading-none tabular-nums text-gray-900">{dd || "?"}</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[13px] font-bold leading-snug text-gray-900">{e.title}</span>
                  <span className="mt-0.5 block truncate text-[11.5px] text-gray-500">
                    {[z.timeLabel, Number(e.event_type) === 2 ? e.location || "In person" : "Online"].filter(Boolean).join(" · ")}
                  </span>
                  {live ? (
                    <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10.5px] font-bold text-red-600 dark:text-red-300">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> Live now
                    </span>
                  ) : countdown(z) ? (
                    <span className="mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10.5px] font-bold" style={{ backgroundColor: alpha(primary, 0.12), color: primary }}>
                      {countdown(z)}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <HomeEmpty compact primary={primary} icon={CalIcon} title="No upcoming sessions" text="New sessions will appear here." />
      )}
    </RailCard>
  );
}

const MEDAL = {
  1: "bg-gradient-to-br from-amber-200 to-amber-500 text-amber-900",
  2: "bg-gradient-to-br from-slate-100 to-slate-400 text-slate-800 dark:from-slate-500 dark:to-slate-300 dark:text-slate-50",
  3: "bg-gradient-to-br from-orange-200 to-orange-600 text-white",
};

export function HomeTopContributors({ top, me, primary, onOpenBoard }) {
  const Trophy = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0" /></svg>
  );
  const list = top || [];
  const meRow = me?.rank && !list.some((r) => r.rank === me.rank) ? me : null;
  return (
    <RailCard
      icon={Trophy}
      title="Top contributors"
      primary={primary}
      tone={{ backgroundColor: "rgba(245, 158, 11, 0.14)", color: "rgb(217, 119, 6)" }}
      action={onOpenBoard && list.length ? <HomeLink primary={primary} onClick={onOpenBoard}>Board</HomeLink> : null}
    >
      {list.length ? (
        <ol className="space-y-1">
          {list.map((r) => (
            <li key={r.member_id} className="flex items-center gap-2.5 rounded-xl px-1.5 py-1.5">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-extrabold ${MEDAL[r.rank] || "bg-gray-100 text-gray-500"}`}>{r.rank}</span>
              <HomeAvatar name={r.name} img={r.img} size={34} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-gray-900">{r.name}</span>
                <span className="block truncate text-[11.5px] text-gray-500">{r.level ? `${r.level.level_name}` : "Getting started"}</span>
              </span>
              <span className="shrink-0 text-[12.5px] font-bold tabular-nums" style={{ color: primary }}>{fmt(r.points)}</span>
            </li>
          ))}
          {meRow ? (
            <li className="mt-1 flex items-center gap-2.5 rounded-xl border border-dashed px-1.5 py-1.5" style={{ borderColor: alpha(primary, 0.4), backgroundColor: alpha(primary, 0.05) }}>
              <span className="grid h-6 min-w-6 shrink-0 place-items-center rounded-full bg-gray-100 px-1 text-[10.5px] font-extrabold text-gray-600">{meRow.rank}</span>
              <HomeAvatar name={meRow.name} img={meRow.img} size={34} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-gray-900">You</span>
                <span className="block truncate text-[11.5px] text-gray-500">{meRow.level?.level_name || "Keep going"}</span>
              </span>
              <span className="shrink-0 text-[12.5px] font-bold tabular-nums" style={{ color: primary }}>{fmt(meRow.points)}</span>
            </li>
          ) : null}
        </ol>
      ) : (
        <HomeEmpty compact primary={primary} icon={Trophy} title="The board is wide open" text="Earn your first points to take the top spot." />
      )}
    </RailCard>
  );
}

const CHAMP_ICON = {
  most_helpful: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z",
  rising_star: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
  top_poster: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z",
};

/**
 * This week's champions: up to three members, one per category, the first in
 * the centre of a podium. Gold is the one fixed colour on the page — it means
 * "first place" in any brand.
 */
export function HomeChampions({ champions, primary }) {
  const list = champions || [];
  // Podium order: second, first, third — the winner stands in the middle.
  const podium = list.length >= 3 ? [list[1], list[0], list[2]] : list.length === 2 ? [list[1], list[0]] : list;

  return (
    <section className={`${cardCls} relative overflow-hidden p-4 sm:p-5`} aria-label="This week's champions">
      <div aria-hidden="true" className="pointer-events-none absolute -right-14 -top-16 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-600 dark:text-amber-300">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7Zm2 13h14v2H5v-2Z" /></svg>
              Weekly recognition
            </p>
            <h3 className="mt-1 text-[17px] font-extrabold tracking-tight text-gray-900">This week’s champions</h3>
            <p className="mt-0.5 text-[12.5px] text-gray-500">Members who showed up for others in the last 7 days.</p>
          </div>
        </div>

        {list.length ? (
          <div className={`mt-4 grid items-end gap-2 ${podium.length === 1 ? "grid-cols-1" : podium.length === 2 ? "grid-cols-2" : "grid-cols-[1fr_1.18fr_1fr]"}`}>
            {podium.map((c) => {
              const first = c === list[0];
              return (
                <div
                  key={c.member_id}
                  className={`flex min-w-0 flex-col items-center gap-1.5 rounded-2xl border px-2 text-center ${
                    first
                      ? "border-amber-300/70 bg-amber-50 pb-3 pt-4 dark:border-amber-400/30 dark:bg-amber-400/10"
                      : "border-gray-200 bg-gray-50/70 pb-2.5 pt-3"
                  }`}
                >
                  <span className="relative">
                    <HomeAvatar name={c.name} img={c.img} size={first ? 50 : 40} ring={first ? "0 0 0 3px rgba(245,158,11,0.55)" : undefined} />
                    {first ? (
                      <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-amber-400 text-[10px] font-extrabold text-amber-950 shadow">1</span>
                    ) : null}
                  </span>
                  <span className="w-full truncate text-[12.5px] font-bold text-gray-900">{(c.name || "").split("@")[0]}</span>
                  <span
                    className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10.5px] font-bold ${first ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : ""}`}
                    style={first ? undefined : { backgroundColor: alpha(primary, 0.1), color: primary }}
                  >
                    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={CHAMP_ICON[c.category] || CHAMP_ICON.rising_star} /></svg>
                    {c.label}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-500">{plural(c.value, c.unit === "reply" ? "reply" : c.unit, c.unit === "reply" ? "replies" : undefined)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4">
            <HomeEmpty
              compact
              primary={primary}
              icon={<svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7Zm2 13h14v2H5v-2Z" /></svg>}
              title="No champions yet this week"
              text="Reply to a post or share a win to claim a spot."
            />
          </div>
        )}
      </div>
    </section>
  );
}
